import type { Character, DialogueAction } from "../manifest/schema.js";
import type { ChatBackend } from "../ai/backend.js";
import type { ChatTurn } from "../ai/prompt.js";
import { MAX_USER_CHARS, TURN_CAP } from "../ai/prompt.js";
import { signOffLine } from "../ai/guard.js";
import { SpeechInput } from "../ai/speech.js";
import type { TtsAdapter } from "../ai/voice.js";
import { clear, h } from "./dom.js";

export interface ChatHooks {
  /** Start/stop the character's talk loop on stage. */
  onSpeaking(speaking: boolean): void;
  /** Model asked to travel (already validated against the character's guide places). */
  onTravel(locationId: string): void;
  /** A scripted quick reply that carries an action (show a product, go somewhere). Runs the action directly. */
  onAction(action: DialogueAction): void;
  announce(text: string): void;
}

export interface Suggestion {
  text: string;
  action?: DialogueAction;
}

/** Free-text conversation with an AI character: log, text input, optional mic, voice on/off. */
export class ChatPanel {
  readonly root = h("section", { class: "dialogue chat", "aria-label": "Conversation", hidden: true, "data-testid": "chat" });
  private readonly log = h("ol", { class: "chat-log", role: "log", "aria-live": "polite", "aria-relevant": "additions", "aria-label": "Messages" });
  private readonly input = h("input", { class: "chat-input", type: "text", id: "chat-input", autocomplete: "off", maxlength: MAX_USER_CHARS, placeholder: "Type your question", "data-testid": "chat-input" });
  private readonly sendBtn = h("button", { class: "btn btn-primary", type: "submit", "data-testid": "chat-send" }, "Send");
  private readonly micBtn = h("button", { class: "btn chat-mic", type: "button", "aria-pressed": "false", "aria-label": "Speak your question", "data-testid": "chat-mic" }, "🎤");
  private readonly voiceBtn = h("button", { class: "btn small", type: "button", "aria-pressed": "true", "data-testid": "chat-voice" }, "Voice on");
  private readonly status = h("p", { class: "chat-status muted small", "aria-live": "polite" });
  private readonly speech = new SpeechInput();
  private history: ChatTurn[] = [];
  private busy = false;
  private voiceOn = true;
  private character: Character | undefined;
  private ended = false;
  private turns = 0;

  constructor(private readonly backend: ChatBackend, private readonly tts: TtsAdapter, private readonly hooks: ChatHooks) {
    this.micBtn.addEventListener("click", () => void this.listen());
    this.voiceBtn.addEventListener("click", () => {
      this.voiceOn = !this.voiceOn;
      if (!this.voiceOn) this.stopSpeaking();
      this.voiceBtn.setAttribute("aria-pressed", String(this.voiceOn));
      this.voiceBtn.textContent = this.voiceOn ? "Voice on" : "Voice off";
    });
    this.root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(c: Character, greeting: string, suggestions: Suggestion[] = []): void {
    this.character = c;
    this.history = [];
    this.ended = false;
    this.turns = 0;
    this.input.disabled = false;
    this.input.placeholder = `Ask ${c.name.split(" ")[0]} a question`;
    clear(this.root);
    clear(this.log);
    const form = h(
      "form",
      { class: "chat-form" },
      h("label", { class: "sr-only", for: "chat-input" }, `Message ${c.name}`),
      this.input,
      this.speech.available ? this.micBtn : null,
      this.sendBtn
    );
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.send(this.input.value);
    });
    this.root.append(
      h(
        "div",
        { class: "dialogue-head" },
        h("strong", {}, c.name),
        h("span", { class: "muted" }, ` ${c.role}`),
        this.backend.mode === "mock" ? h("span", { class: "chat-badge", title: "No AI backend configured: scripted demo replies" }, "Demo mode") : null,
        h("span", { class: "chat-head-actions" }, this.voiceBtn, h("button", { class: "btn small", type: "button", "aria-label": "End conversation", onclick: () => this.close() }, "Leave"))
      ),
      this.log,
      suggestions.length
        ? h("div", { class: "choices chat-suggest", role: "group", "aria-label": "Suggestions" }, ...suggestions.map((t) => h("button", { class: "btn choice small", type: "button", onclick: () => this.quickReply(t) }, t.text)))
        : "",
      this.status,
      form
    );
    this.root.hidden = false;
    this.add("assistant", greeting);
    void this.say(greeting);
    this.input.focus();
  }

  close(): void {
    this.speech.stop();
    this.stopSpeaking();
    this.root.hidden = true;
    clear(this.root);
  }

  async send(raw: string): Promise<void> {
    const text = raw.trim().slice(0, MAX_USER_CHARS);
    if (!text || this.busy || this.ended || !this.character) return;
    this.input.value = "";
    this.add("user", text);
    this.turns++;
    if (this.turns > TURN_CAP) return this.finish(signOffLine(this.character.name.split(" ")[0] ?? this.character.name));
    this.setBusy(true);
    try {
      const r = await this.backend.reply(this.history);
      if (this.root.hidden) return;
      this.add("assistant", r.reply);
      if (r.ended) this.lock();
      if (r.travelTo) {
        const to = r.travelTo;
        await this.say(r.reply, r.sig);
        this.close();
        this.hooks.onTravel(to);
        return;
      }
      void this.say(r.reply, r.sig);
    } catch (err) {
      this.status.textContent = `${this.character.name.split(" ")[0]} didn't catch that (${err instanceof Error ? err.message : "error"}). Please try again.`;
    } finally {
      this.setBusy(false);
    }
  }

  /** A scripted option: run its action directly (no model call); plain options are sent as a question. */
  private quickReply(t: Suggestion): void {
    if (this.busy || this.ended) return;
    if (!t.action) return void this.send(t.text);
    this.add("user", t.text);
    this.stopSpeaking();
    this.hooks.onAction(t.action);
  }

  private lock(): void {
    this.ended = true;
    this.input.disabled = true;
    this.input.placeholder = "This conversation has ended";
    this.sendBtn.disabled = true;
  }

  private finish(line: string): void {
    this.add("assistant", line);
    this.lock();
    void this.say(line);
  }

  private add(role: ChatTurn["role"], content: string): void {
    this.history.push({ role, content });
    const who = role === "user" ? "You" : (this.character?.name.split(" ")[0] ?? "");
    this.log.append(h("li", { class: `chat-msg chat-msg--${role}` }, h("span", { class: "chat-who" }, who), h("span", { class: "chat-bubble" }, content)));
    this.log.scrollTop = this.log.scrollHeight;
  }

  private setBusy(b: boolean): void {
    this.busy = b;
    this.sendBtn.disabled = b || this.ended;
    this.root.setAttribute("aria-busy", String(b));
    this.status.textContent = b ? `${this.character?.name.split(" ")[0] ?? ""} is thinking…` : "";
  }

  private async say(text: string, sig?: string): Promise<void> {
    this.hooks.onSpeaking(true);
    try {
      if (this.voiceOn) await this.tts.speak(text, sig);
      else await new Promise((r) => setTimeout(r, Math.min(4000, 800 + text.length * 40)));
    } finally {
      this.hooks.onSpeaking(false);
    }
  }

  private stopSpeaking(): void {
    this.tts.cancel();
    this.hooks.onSpeaking(false);
  }

  private async listen(): Promise<void> {
    if (this.speech.listening) return this.speech.stop();
    this.stopSpeaking();
    this.micBtn.setAttribute("aria-pressed", "true");
    this.micBtn.classList.add("is-listening");
    this.status.textContent = "Listening…";
    const text = await this.speech.listen((t) => (this.input.value = t));
    this.micBtn.setAttribute("aria-pressed", "false");
    this.micBtn.classList.remove("is-listening");
    this.status.textContent = "";
    if (text) await this.send(text);
  }
}
